import React from 'react'
import PDFViewerSimple from './PDFViewerSimple'
import ImageViewer from './ImageViewer'
import ExcelViewer from './ExcelViewer'
import { FileText, Download } from 'lucide-react'

interface Highlight {
  id: string
  text: string
  startIndex: number
  length: number
  page: number
}

interface FileViewerProps {
  fileUrl: string
  fileName?: string
  fileType?: string
  highlights?: Highlight[]
  onHighlightClick?: (highlight: Highlight) => void
  onPageChange?: (page: number) => void
  currentPage?: number
  className?: string
}

/**
 * Dynamic file viewer that renders different viewers based on file type.
 * Supports: PDF, Images (PNG, JPG, JPEG), Excel (XLSX, XLS, XLSM)
 */
export const FileViewer: React.FC<FileViewerProps> = ({
  fileUrl,
  fileName = '',
  fileType = '',
  highlights = [],
  onHighlightClick,
  onPageChange,
  currentPage = 1,
  className = ''
}) => {
  /**
   * Convert MIME type to file extension
   */
  const mimeTypeToExtension = (mimeType: string): string => {
    const mimeMap: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.ms-excel.sheet.macroEnabled.12': 'xlsm',
      'text/csv': 'csv',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/msword': 'doc'
    }
    return mimeMap[mimeType.toLowerCase()] || ''
  }

  /**
   * Detect file type from fileName or fileType prop
   * Returns normalized file extension
   * PRIORITY: fileName > fileType > URL (because backend sometimes has incorrect fileType)
   */
  const getFileExtension = (): string => {
    // PRIORITY 1: Extract from fileName (most reliable)
    if (fileName) {
      const parts = fileName.split('.')
      if (parts.length > 1) {
        const ext = parts[parts.length - 1].toLowerCase()
        console.log('FileViewer: Detected extension from fileName:', fileName, '->', ext)
        return ext
      }
    }

    // PRIORITY 2: Try fileType prop
    if (fileType) {
      const cleanType = fileType.toLowerCase().replace('.', '')

      // Check if it's already an extension (e.g., "xlsx", "png")
      if (['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'xlsx', 'xls', 'xlsm', 'csv', 'docx', 'doc'].includes(cleanType)) {
        console.log('FileViewer: Detected extension from fileType:', cleanType)
        return cleanType
      }

      // Try to convert MIME type to extension
      const extFromMime = mimeTypeToExtension(fileType)
      if (extFromMime) {
        console.log('FileViewer: Converted MIME type to extension:', fileType, '->', extFromMime)
        return extFromMime
      }
    }

    // PRIORITY 3: Try to extract from URL (last resort)
    if (fileUrl && !fileUrl.startsWith('blob:')) {
      const urlParts = fileUrl.split('/')
      const lastPart = urlParts[urlParts.length - 1]
      const fileParts = lastPart.split('.')
      if (fileParts.length > 1) {
        // Remove query parameters
        const ext = fileParts[fileParts.length - 1].split('?')[0].toLowerCase()
        console.log('FileViewer: Detected extension from URL:', ext)
        return ext
      }
    }

    console.warn('FileViewer: Could not detect file extension', { fileType, fileName, fileUrl })
    return ''
  }

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = fileName || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (!fileUrl) {
    return (
      <div className={`bg-white rounded border border-[#b2bbbc] shadow-lg h-full flex items-center justify-center ${className}`}>
        <div className="text-center p-6">
          <div className="w-24 h-24 mx-auto mb-6">
            <div className="w-full h-full bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="w-12 h-12 text-blue-600" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-center mb-3">No File Available</h3>
          <p className="text-center text-gray-600">
            Unable to load the selected file
          </p>
        </div>
      </div>
    )
  }

  const extension = getFileExtension()
  console.log('FileViewer: Routing to viewer - extension:', extension, 'fileName:', fileName, 'fileType:', fileType)

  // Route to appropriate viewer based on file type
  // PDF files
  if (extension === 'pdf') {
    console.log('FileViewer: Rendering PDFViewerSimple')
    return (
      <div className={`w-full h-full bg-white rounded border border-[#b2bbbc] shadow-lg relative ${className}`}>
        <PDFViewerSimple
          pdfUrl={fileUrl}
          highlights={highlights}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onHighlightClick={onHighlightClick}
        />
      </div>
    )
  }

  // Image files
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
    console.log('FileViewer: Rendering ImageViewer')
    return (
      <div className={`w-full h-full bg-white rounded border border-[#b2bbbc] shadow-lg relative ${className}`}>
        <ImageViewer
          imageUrl={fileUrl}
          fileName={fileName}
          highlights={highlights}
          onHighlightClick={onHighlightClick}
        />
      </div>
    )
  }

  // Excel files
  if (['xlsx', 'xls', 'xlsm', 'csv'].includes(extension)) {
    console.log('FileViewer: Rendering ExcelViewer')
    return (
      <div className={`w-full h-full bg-white rounded border border-[#b2bbbc] shadow-lg relative ${className}`}>
        <ExcelViewer
          fileUrl={fileUrl}
          fileName={fileName}
          highlights={highlights}
          onHighlightClick={onHighlightClick}
        />
      </div>
    )
  }

  // Unsupported file types (DOCX, DOC, etc.) - Show download option
  console.log('FileViewer: Rendering download-only view for unsupported extension:', extension)
  return (
    <div className={`bg-white rounded border border-[#b2bbbc] shadow-lg h-full flex items-center justify-center ${className}`}>
      <div className="text-center p-6 max-w-md">
        <div className="w-24 h-24 mx-auto mb-6">
          <div className="w-full h-full bg-gray-100 rounded-full flex items-center justify-center">
            <FileText className="w-12 h-12 text-gray-600" />
          </div>
        </div>
        <h3 className="text-xl font-bold text-center mb-3">Preview Not Available</h3>
        <p className="text-center text-gray-600 mb-2">
          This file type ({extension.toUpperCase() || 'unknown'}) cannot be previewed in the browser.
        </p>
        <p className="text-sm text-gray-500 mb-6">
          {fileName}
        </p>
        <button
          onClick={handleDownload}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
        >
          <Download className="w-4 h-4" />
          Download File
        </button>

        {/* Highlight info overlay - if evidence was found */}
        {highlights.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <div className="text-sm font-semibold text-gray-800">
                Evidence Located in This File
              </div>
            </div>
            <div className="text-xs text-gray-700 bg-white p-2 rounded border">
              <div className="font-medium mb-1">Evidence:</div>
              <div className="italic">
                "{highlights[0].text.substring(0, 200)}{highlights[0].text.length > 200 ? '...' : ''}"
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FileViewer
